import { useState, useEffect } from "react";
import {
  IonGrid,
  IonRow,
  IonCol,
  IonItem,
  IonInput,
  IonTextarea,
  IonLabel,
  IonToggle,
  IonAvatar,
  IonButton,
  IonIcon,
  IonSelect,
  IonSelectOption,
} from "@ionic/react";
import { saveOutline } from "ionicons/icons";
import { useQueryClient } from "@tanstack/react-query";
import { usePymbleApi } from "../../hooks/usePymbleApi";
import { useDictionary } from "../../hooks/useDictionary";
import { useSelectIconPopover } from "../../hooks/useSelectIconPopover";
import { displayMsg } from "pymble-ui/components";
import type { GroupDetail } from "../../hooks/useGroupDetail";
import { parseISO, formatISO, format } from "date-fns";

import "./SettingsTab.css";

interface GroupTypeEntry {
  TypeId?: number;
  Name?: string;
  [key: string]: unknown;
}

interface SettingsTabProps {
  groupId: string;
  group: GroupDetail;
}

const toDateInput = (dateStr?: string | null): string => {
  if (!dateStr) return "";
  try {
    return format(parseISO(dateStr), "yyyy-MM-dd");
  } catch {
    return "";
  }
};

const toISO = (dateStr: string | null): string | null => {
  if (!dateStr) return null;
  try {
    return formatISO(parseISO(dateStr));
  } catch {
    return null;
  }
};

const SettingsTab: React.FC<SettingsTabProps> = ({ groupId, group }) => {
  const { callApi } = usePymbleApi();
  const queryClient = useQueryClient();
  const { dictionary: groupTypes, dictionaryLoaded } =
    useDictionary("GroupTypes");
  const { presentIcons, selectedIcon, IconsPopover } =
    useSelectIconPopover();

  const [saving, setSaving] = useState(false);

  // Form state
  const [nameShort, setNameShort] = useState(group.Name?.Short ?? "");
  const [nameLong, setNameLong] = useState(group.Name?.Long ?? "");
  const [descShort, setDescShort] = useState(group.Description?.Short ?? "");
  const [descLong, setDescLong] = useState(group.Description?.Long ?? "");
  const [icon, setIcon] = useState(group.Icon ?? "");
  // Type.Id is widened in GroupDetail to `number | string` because preset-
  // typed groups use the literal "preset". The Settings dropdown only edits
  // numeric type ids so we coerce here - presets reset the field to
  // undefined and the save handler below skips the Type block entirely so a
  // stray save can never clobber `Type.Id === "preset"` server-side
  // (closes group-manager#81; defence-in-depth alongside the
  // GroupDetail-level redirect that normally prevents a preset from
  // mounting the legacy Settings tab in the first place).
  const isPresetGroup = group.Type?.Id === "preset";
  const initialTypeId =
    typeof group.Type?.Id === "number" ? group.Type.Id : undefined;
  const [typeId, setTypeId] = useState<number | undefined>(initialTypeId);
  const [typeLabel, setTypeLabel] = useState(group.Type?.Label ?? "");
  const [showMemberList, setShowMemberList] = useState(
    group.ShowMemberList === true || group.ShowMemberList === "true"
  );
  const [chatEnabled, setChatEnabled] = useState(
    group.Chat === true || group.Chat === "true"
  );
  const [startAt, setStartAt] = useState(toDateInput(group.StartAt));
  const [endAt, setEndAt] = useState(toDateInput(group.EndAt));
  // CanSubscribe is a role array (["parent","staff","student"]) on most groups,
  // but legacy/system groups store a boolean or omit it. Guard with Array.isArray
  // before calling .includes — optional chaining alone is not enough, because a
  // present-but-non-array value like `false` is not nullish, so `false?.includes`
  // still evaluates `false.includes` and throws "includes is not a function".
  const subscribeRoles = Array.isArray(group.CanSubscribe) ? group.CanSubscribe : [];
  const [carerSubscribe, setCarerSubscribe] = useState(
    subscribeRoles.includes("parent")
  );
  const [staffSubscribe, setStaffSubscribe] = useState(
    subscribeRoles.includes("staff")
  );
  const [studentSubscribe, setStudentSubscribe] = useState(
    subscribeRoles.includes("student")
  );

  // Sync icon selection from popover
  useEffect(() => {
    if (selectedIcon?.img) {
      setIcon(selectedIcon.img);
    }
  }, [selectedIcon]);

  const handleSave = async () => {
    if (!nameShort.trim()) {
      displayMsg("Name Short is required", {
        type: "warning",
        position: "top",
        duration: 2000,
      });
      return;
    }

    setSaving(true);
    try {
      const canSubscribe: string[] = [];
      if (carerSubscribe) canSubscribe.push("parent");
      if (staffSubscribe) canSubscribe.push("staff");
      if (studentSubscribe) canSubscribe.push("student");

      // For preset-typed groups, leave the Type block out entirely so a
      // stray save cannot overwrite `Type.Id === "preset"` with the
      // dropdown's coerced numeric value (closes group-manager#81). The
      // GroupDetail-level redirect normally keeps preset groups off this
      // tab, but this is defence-in-depth.
      const data: Record<string, unknown> = {
        _id: groupId,
        Name: { Short: nameShort, Long: nameLong },
        Description: { Short: descShort, Long: descLong },
        Icon: icon || undefined,
        StartAt: toISO(startAt),
        EndAt: toISO(endAt),
        ShowMemberList: showMemberList,
        Chat: chatEnabled,
        CanSubscribe: canSubscribe,
      };
      if (!isPresetGroup) {
        data.Type = {
          Id: typeId ? parseInt(String(typeId)) : undefined,
          Label: typeLabel,
        };
      }

      await callApi("PUT", `/group/admin/${groupId}`, { data });

      queryClient.invalidateQueries({ queryKey: ["group-detail", groupId] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });

      displayMsg("Settings saved", {
        type: "success",
        position: "top",
        duration: 1500,
      });
    } catch (err) {
      // The server refuses the whole update with 403 "Group is not eligible
      // for chat" when chat would be enabled on a student-audience group
      // (is_chat_eligible_group in pymble-apis is authoritative - eligibility
      // is deliberately NOT reimplemented here). Surface that specifically
      // and revert the toggle to the stored flag; nothing was saved.
      const resp = (
        err as { response?: { status?: number; data?: { detail?: string } } }
      )?.response;
      if (
        resp?.status === 403 &&
        typeof resp.data?.detail === "string" &&
        resp.data.detail.includes("not eligible for chat")
      ) {
        setChatEnabled(group.Chat === true || group.Chat === "true");
        displayMsg(
          "This group isn't eligible for chat (student audience) - settings were not saved.",
          {
            type: "warning",
            position: "top",
            duration: 4000,
          }
        );
      } else {
        displayMsg("Failed to save settings", {
          type: "danger",
          position: "top",
          duration: 3000,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-tab">
      <IonGrid>
        <IonRow>
          {/* Left column: text fields */}
          <IonCol size="12" sizeMd="6">
            <IonItem>
              <IonInput
                label="Name Short"
                labelPlacement="stacked"
                value={nameShort}
                onIonInput={(e) => setNameShort(e.detail.value || "")}
                placeholder="Short name for the group"
              />
            </IonItem>
            <IonItem>
              <IonInput
                label="Name Long"
                labelPlacement="stacked"
                value={nameLong}
                onIonInput={(e) => setNameLong(e.detail.value || "")}
                placeholder="Full name for the group"
              />
            </IonItem>
            <IonItem>
              <IonInput
                label="Description Short"
                labelPlacement="stacked"
                value={descShort}
                onIonInput={(e) => setDescShort(e.detail.value || "")}
                placeholder="Short description"
              />
            </IonItem>
            <IonItem>
              <IonTextarea
                label="Description Long"
                labelPlacement="stacked"
                value={descLong}
                onIonInput={(e) => setDescLong(e.detail.value || "")}
                placeholder="Full description"
                rows={4}
              />
            </IonItem>
          </IonCol>

          {/* Right column: icon, type, toggles, dates */}
          <IonCol size="12" sizeMd="6">
            <IonRow className="ion-align-items-center">
              <IonCol size="auto">
                <IonItem lines="none" className="settings-icon-item">
                  <IonLabel color="primary">Icon</IonLabel>
                  <IonAvatar
                    className="settings-icon-avatar"
                    onClick={(e) => presentIcons({ event: e.nativeEvent })}
                  >
                    <img src={icon} alt="group icon" />
                  </IonAvatar>
                </IonItem>
              </IonCol>
              <IonCol>
                <IonItem lines="none">
                  <IonLabel color="primary" position="stacked">
                    Group Type
                  </IonLabel>
                  {dictionaryLoaded ? (
                    <IonSelect
                      value={typeId}
                      onIonChange={(e) => {
                        const id = e.detail.value;
                        setTypeId(id);
                        const gtype = (groupTypes as GroupTypeEntry[]).find(
                          (t) => t.TypeId === id
                        );
                        setTypeLabel(gtype?.Name ?? "");
                      }}
                      placeholder="Select Type"
                      interface="popover"
                    >
                      {(groupTypes as GroupTypeEntry[]).map((gt) => (
                        <IonSelectOption key={gt.TypeId} value={gt.TypeId}>
                          {gt.Name}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  ) : (
                    <IonLabel color="medium">Loading types...</IonLabel>
                  )}
                </IonItem>
              </IonCol>
            </IonRow>

            <IonItem lines="none">
              <IonLabel color="primary">Show Member List</IonLabel>
              <IonToggle
                checked={showMemberList}
                onIonChange={() => setShowMemberList(!showMemberList)}
                color="secondary"
              />
            </IonItem>

            <IonItem lines="none">
              <IonLabel color="primary">Group Chat</IonLabel>
              <IonToggle
                aria-label="Group Chat"
                checked={chatEnabled}
                onIonChange={() => setChatEnabled(!chatEnabled)}
                color="secondary"
              />
            </IonItem>

            <IonRow>
              <IonCol size="6">
                <IonItem>
                  <IonInput
                    label="Start At"
                    labelPlacement="stacked"
                    type="date"
                    value={startAt}
                    onIonInput={(e) => setStartAt(e.detail.value || "")}
                  />
                </IonItem>
              </IonCol>
              <IonCol size="6">
                <IonItem>
                  <IonInput
                    label="End At"
                    labelPlacement="stacked"
                    type="date"
                    value={endAt}
                    onIonInput={(e) => setEndAt(e.detail.value || "")}
                  />
                </IonItem>
              </IonCol>
            </IonRow>

            <div className="settings-section-label">Who can subscribe?</div>
            <IonRow>
              <IonCol>
                <IonItem lines="none">
                  <IonLabel>Carer</IonLabel>
                  <IonToggle
                    checked={carerSubscribe}
                    onIonChange={() => setCarerSubscribe(!carerSubscribe)}
                    color="secondary"
                  />
                </IonItem>
              </IonCol>
              <IonCol>
                <IonItem lines="none">
                  <IonLabel>Staff</IonLabel>
                  <IonToggle
                    checked={staffSubscribe}
                    onIonChange={() => setStaffSubscribe(!staffSubscribe)}
                    color="secondary"
                  />
                </IonItem>
              </IonCol>
              <IonCol>
                <IonItem lines="none">
                  <IonLabel>Student</IonLabel>
                  <IonToggle
                    checked={studentSubscribe}
                    onIonChange={() => setStudentSubscribe(!studentSubscribe)}
                    color="secondary"
                  />
                </IonItem>
              </IonCol>
            </IonRow>

          </IonCol>
        </IonRow>

        {/* Save — full width, right-aligned */}
        <IonRow>
          <IonCol className="settings-save-col">
            <IonButton
              onClick={handleSave}
              disabled={saving}
              color="primary"
              className="ion-text-capitalize"
            >
              <IonIcon slot="start" icon={saveOutline} />
              {saving ? "Saving..." : "Save Settings"}
            </IonButton>
          </IonCol>
        </IonRow>
      </IonGrid>

      {/* Group ID — small, muted, single-click-selectable footer (user-select:
          all) so an admin can quickly grab the id. Parity with the old group
          manager's detail screen (group-manager#91). */}
      <div className="settings-group-id">
        <span className="settings-group-id-label">Group ID</span>
        <code className="settings-group-id-value">{groupId}</code>
      </div>

      {IconsPopover}
    </div>
  );
};

export default SettingsTab;
